/**
 * AI Insights Card Component
 * AI予測・推奨・アラートを表示するインサイトカード
 */

import { Card, Group, Text, ThemeIcon, Badge, Timeline, ActionIcon, Tooltip, useMantineColorScheme } from '@mantine/core';
import {
  IconBrain,
  IconTrendingUp,
  IconAlertTriangle,
  IconBulb,
  IconRefresh,
} from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getGlassStyle } from '../styles/darkModeGlass';

const MotionCard = motion(Card);

type Insight = {
  type: 'prediction' | 'recommendation' | 'alert';
  title: string;
  description: string;
  confidence: number;
  icon: React.ReactNode;
  color: string;
};

export const AIInsightsCard = () => {
  const { colorScheme } = useMantineColorScheme();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // モックインサイト生成（将来的にはAI APIから取得）
    const mockInsights: Insight[] = [
      {
        type: 'prediction',
        title: '今週末は混雑が予想されます',
        description: '土曜日15-17時の入室予測: 85名（+23% vs 平均）',
        confidence: 85,
        icon: <IconTrendingUp size={16} />,
        color: 'blue',
      },
      {
        type: 'recommendation',
        title: '月曜15時台のクラス追加を推奨',
        description: '需要予測: +23% | 収益見込み: +¥45,000/月',
        confidence: 78,
        icon: <IconBulb size={16} />,
        color: 'yellow',
      },
      {
        type: 'alert',
        title: '退会リスク顧客: 3名検出',
        description: '過去30日の出席率低下。早期フォロー推奨',
        confidence: 92,
        icon: <IconAlertTriangle size={16} />,
        color: 'red',
      },
    ];
    setInsights(mockInsights);
  }, []);

  const refreshInsights = () => {
    setIsLoading(true);
    // 将来的にはAI APIを呼び出し
    setTimeout(() => setIsLoading(false), 1000);
  };

  return (
    <MotionCard
      padding="sm"
      radius="sm"
      shadow="sm"
      style={{
        ...getGlassStyle(colorScheme),
        minHeight: '240px',
      }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="lg" radius="md" color="violet" variant="light">
            <IconBrain size={20} />
          </ThemeIcon>
          <Text size="lg" fw={700}>
            AIインサイト(モックデータ)
          </Text>
        </Group>
        <Tooltip label="更新">
          <ActionIcon variant="light" color="gray" onClick={refreshInsights} loading={isLoading}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Timeline active={insights.length} bulletSize={24} lineWidth={2}>
        {insights.map((insight, i) => (
          <Timeline.Item
            key={i}
            bullet={insight.icon}
            title={
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  {insight.title}
                </Text>
                <Badge size="xs" color={insight.color} variant="light">
                  信頼度 {insight.confidence}%
                </Badge>
              </Group>
            }
          >
            <Text size="xs" c="dimmed" mt={4}>
              {insight.description}
            </Text>
          </Timeline.Item>
        ))}
      </Timeline>
    </MotionCard>
  );
};
